import { useState, useEffect } from "react";
import { format, parseISO, addDays, startOfWeek } from "date-fns";
import { fr } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { ScrollArea } from "../ui/scroll-area";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "../ui/drawer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Mail,
  BookOpen,
  Plus,
  Send,
  FileSignature,
  Check,
  Clock,
  Calendar,
  Trash2,
  Eye,
  Users,
  Camera,
  Heart,
  MessageCircle,
  UserCheck,
  CalendarClock,
  FileText,
  Upload,
  X,
  Loader2,
  AlertTriangle,
  Image as ImageIcon,
  Paperclip,
  Images,
  Key,
  Copy,
  RefreshCw,
  Download,
  Grid,
  List,
} from "lucide-react";
import { DynamicSubjectSelector } from "./DynamicSubjectSelector";
import {
  // Carnet de liaison
  createLiaisonMessage,
  listLiaisonMessages,
  publishLiaisonMessage,
  deleteLiaisonMessage,
  getMessageSignatures,
  // Cahier de texte
  createHomework,
  listHomework,
  deleteHomework,
  // Cahier de vie
  listLifeEntries,
  createLifeEntry,
  updateLifeEntry,
  deleteLifeEntry,
  publishLifeEntry,
  toggleLikeEntry,
  getLifeEntryTypes,
  // Suivi élèves
  listStudentNotes,
  createStudentNote,
  updateStudentNote,
  deleteStudentNote,
  getStudentNoteTypes,
  getStudentNotesByStudent,
  // Réunions parents
  listMeetings,
  createMeeting,
  updateMeeting,
  deleteMeeting,
  updateMeetingStatus,
  // Common
  listStudents,
  getCartableSummary,
  // Storage
  uploadFile,
  getFileUrl,
  // Albums
  createAlbum,
  listAlbums,
  getAlbum,
  updateAlbum,
  deleteAlbum,
  publishAlbum,
  addPhotoToAlbum,
  removePhotoFromAlbum,
  // Family codes
  generateFamilyCode,
  listFamilyCodes,
  revokeFamilyCode,
  regenerateFamilyCode,
} from "../../lib/api";
import { toast } from "sonner";

const PRIORITY_OPTIONS = [
  { value: "normal", label: "Normal", color: "bg-slate-100 text-slate-700" },
  { value: "important", label: "Important", color: "bg-amber-100 text-amber-700" },
  { value: "urgent", label: "Urgent", color: "bg-red-100 text-red-700" },
];

const LIFE_ENTRY_TYPES = [
  { value: "moment", label: "Moment de classe", emoji: "✨" },
  { value: "sortie", label: "Sortie scolaire", emoji: "🚌" },
  { value: "projet", label: "Projet", emoji: "🎨" },
  { value: "evenement", label: "Événement", emoji: "🎉" },
  { value: "celebration", label: "Célébration", emoji: "🎂" },
];

const NOTE_TYPES = [
  { value: "observation", label: "Observation", color: "bg-blue-100 text-blue-700" },
  { value: "evaluation", label: "Évaluation", color: "bg-green-100 text-green-700" },
  { value: "comportement", label: "Comportement", color: "bg-amber-100 text-amber-700" },
  { value: "reunion", label: "Réunion parents", color: "bg-purple-100 text-purple-700" },
  { value: "aide", label: "Aide/Soutien", color: "bg-indigo-100 text-indigo-700" },
];

const MEETING_STATUSES = [
  { value: "planifie", label: "Planifié", color: "bg-blue-100 text-blue-700" },
  { value: "confirme", label: "Confirmé", color: "bg-green-100 text-green-700" },
  { value: "annule", label: "Annulé", color: "bg-red-100 text-red-700" },
  { value: "termine", label: "Terminé", color: "bg-slate-100 text-slate-700" },
];

// Responsive Modal Helper
function ResponsiveModal({ open, onOpenChange, title, children, footer }) {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);
  
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[90vh]">
          <DrawerHeader>
            <DrawerTitle>{title}</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-4 overflow-y-auto">{children}</div>
          {footer && <DrawerFooter>{footer}</DrawerFooter>}
        </DrawerContent>
      </Drawer>
    );
  }
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {children}
        {footer && <DialogFooter>{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}

export function CartableModule({ teacherCycles = [] }) {
  const [activeTab, setActiveTab] = useState("liaison");
  const [messages, setMessages] = useState([]);
  const [homework, setHomework] = useState([]);
  const [lifeEntries, setLifeEntries] = useState([]);
  const [studentNotes, setStudentNotes] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [students, setStudents] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [familyCodes, setFamilyCodes] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [showNewHomework, setShowNewHomework] = useState(false);
  const [showNewLifeEntry, setShowNewLifeEntry] = useState(false);
  const [showNewNote, setShowNewNote] = useState(false);
  const [showNewMeeting, setShowNewMeeting] = useState(false);
  const [showNewAlbum, setShowNewAlbum] = useState(false);
  const [showAlbumDetail, setShowAlbumDetail] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [showSignatures, setShowSignatures] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [signatures, setSignatures] = useState([]);

  // Fetch all data
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [messagesRes, homeworkRes, studentsRes, summaryRes, lifeRes, notesRes, meetingsRes, albumsRes, codesRes] = await Promise.all([
        listLiaisonMessages({ limit: 50 }),
        listHomework({ upcoming: true, limit: 50 }),
        listStudents(),
        getCartableSummary(),
        listLifeEntries({ limit: 50 }),
        listStudentNotes({ limit: 50 }),
        listMeetings({ upcoming: true, limit: 50 }),
        listAlbums({ limit: 50 }),
        listFamilyCodes(),
      ]);
      setMessages(messagesRes.data?.messages || []);
      setHomework(homeworkRes.data?.homework || []);
      setStudents(studentsRes.data || []);
      setSummary(summaryRes.data || {});
      setLifeEntries(lifeRes.data?.entries || []);
      setStudentNotes(notesRes.data?.notes || []);
      setMeetings(meetingsRes.data?.meetings || []);
      setAlbums(albumsRes.data?.albums || []);
      setFamilyCodes(codesRes.data?.codes || []);
    } catch (err) {
      console.error("Erreur chargement cartable:", err);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const refreshMessages = async () => {
    try {
      const res = await listLiaisonMessages({ limit: 50 });
      setMessages(res.data?.messages || []);
    } catch (err) {
      console.error("Erreur refresh messages:", err);
    }
  };

  const refreshHomework = async () => {
    try {
      const res = await listHomework({ upcoming: true, limit: 50 });
      setHomework(res.data?.homework || []);
    } catch (err) {
      console.error("Erreur refresh homework:", err);
    }
  };

  const refreshLifeEntries = async () => {
    try {
      const res = await listLifeEntries({ limit: 50 });
      setLifeEntries(res.data?.entries || []);
    } catch (err) {
      console.error("Erreur refresh life entries:", err);
    }
  };

  const refreshNotes = async () => {
    try {
      const res = await listStudentNotes({ limit: 50 });
      setStudentNotes(res.data?.notes || []);
    } catch (err) {
      console.error("Erreur refresh notes:", err);
    }
  };

  const refreshMeetings = async () => {
    try {
      const res = await listMeetings({ upcoming: true, limit: 50 });
      setMeetings(res.data?.meetings || []);
    } catch (err) {
      console.error("Erreur refresh meetings:", err);
    }
  };

  const refreshAlbums = async () => {
    try {
      const res = await listAlbums({ limit: 50 });
      setAlbums(res.data?.albums || []);
    } catch (err) {
      console.error("Erreur refresh albums:", err);
    }
  };

  const refreshFamilyCodes = async () => {
    try {
      const res = await listFamilyCodes();
      setFamilyCodes(res.data?.codes || []);
    } catch (err) {
      console.error("Erreur refresh family codes:", err);
    }
  };

  const handlePublishMessage = async (messageId) => {
    try {
      await publishLiaisonMessage(messageId);
      toast.success("Message publié");
      refreshMessages();
    } catch (err) {
      console.error("Erreur publication:", err);
      toast.error("Erreur lors de la publication");
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm("Supprimer ce message ?")) return;
    try {
      await deleteLiaisonMessage(messageId);
      toast.success("Message supprimé");
      refreshMessages();
    } catch (err) {
      console.error("Erreur suppression:", err);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleDeleteHomework = async (homeworkId) => {
    if (!window.confirm("Supprimer ce devoir ?")) return;
    try {
      await deleteHomework(homeworkId);
      toast.success("Devoir supprimé");
      refreshHomework();
    } catch (err) {
      console.error("Erreur suppression:", err);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleDeleteLifeEntry = async (entryId) => {
    if (!window.confirm("Supprimer cette entrée ?")) return;
    try {
      await deleteLifeEntry(entryId);
      toast.success("Entrée supprimée");
      refreshLifeEntries();
    } catch (err) {
      console.error("Erreur suppression:", err);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Supprimer cette note ?")) return;
    try {
      await deleteStudentNote(noteId);
      toast.success("Note supprimée");
      refreshNotes();
    } catch (err) {
      console.error("Erreur suppression:", err);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleDeleteMeeting = async (meetingId) => {
    if (!window.confirm("Supprimer cette réunion ?")) return;
    try {
      await deleteMeeting(meetingId);
      toast.success("Réunion supprimée");
      refreshMeetings();
    } catch (err) {
      console.error("Erreur suppression:", err);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleDeleteAlbum = async (albumId) => {
    if (!window.confirm("Supprimer cet album ?")) return;
    try {
      await deleteAlbum(albumId);
      toast.success("Album supprimé");
      refreshAlbums();
    } catch (err) {
      console.error("Erreur suppression:", err);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handlePublishAlbum = async (albumId) => {
    try {
      await publishAlbum(albumId);
      toast.success("Album publié");
      refreshAlbums();
    } catch (err) {
      console.error("Erreur publication:", err);
      toast.error("Erreur lors de la publication");
    }
  };

  const handleGenerateFamilyCode = async (studentId) => {
    try {
      const res = await generateFamilyCode(studentId);
      toast.success(`Code généré : ${res.data.code}`);
      refreshFamilyCodes();
    } catch (err) {
      console.error("Erreur génération code:", err);
      toast.error("Erreur lors de la génération");
    }
  };

  const handleRevokeFamilyCode = async (codeId) => {
    if (!window.confirm("Révoquer ce code ?")) return;
    try {
      await revokeFamilyCode(codeId);
      toast.success("Code révoqué");
      refreshFamilyCodes();
    } catch (err) {
      console.error("Erreur révocation:", err);
      toast.error("Erreur lors de la révocation");
    }
  };

  const handleRegenerateFamilyCode = async (codeId) => {
    try {
      const res = await regenerateFamilyCode(codeId);
      toast.success(`Nouveau code : ${res.data.code}`);
      refreshFamilyCodes();
    } catch (err) {
      console.error("Erreur régénération:", err);
      toast.error("Erreur lors de la régénération");
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Code copié !");
  };

  const handleViewSignatures = async (message) => {
    setSelectedMessage(message);
    try {
      const res = await getMessageSignatures(message.id);
      setSignatures(res.data?.signatures || []);
      setShowSignatures(true);
    } catch (err) {
      console.error("Erreur chargement signatures:", err);
      toast.error("Erreur");
    }
  };

  // Stats
  const draftMessages = messages.filter(m => m.status === "draft").length;
  const publishedMessages = messages.filter(m => m.status === "published").length;
  const upcomingHomework = homework.length;
  const unpublishedLife = lifeEntries.filter(e => !e.is_published).length;
  const upcomingMeetings = meetings.filter(m => m.status !== "annule" && m.status !== "termine").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Cartable numérique</h2>
          <p className="text-sm text-slate-500">Communication école-famille</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <Card className="bg-indigo-50 border-indigo-200">
          <CardContent className="p-3 text-center">
            <Mail className="w-5 h-5 mx-auto mb-1 text-indigo-600" />
            <p className="text-xl font-bold text-indigo-700">{publishedMessages}</p>
            <p className="text-[10px] text-indigo-600">Messages</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-3 text-center">
            <BookOpen className="w-5 h-5 mx-auto mb-1 text-green-600" />
            <p className="text-xl font-bold text-green-700">{upcomingHomework}</p>
            <p className="text-[10px] text-green-600">Devoirs</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-3 text-center">
            <Camera className="w-5 h-5 mx-auto mb-1 text-amber-600" />
            <p className="text-xl font-bold text-amber-700">{lifeEntries.length}</p>
            <p className="text-[10px] text-amber-600">Vie de classe</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-3 text-center">
            <UserCheck className="w-5 h-5 mx-auto mb-1 text-purple-600" />
            <p className="text-xl font-bold text-purple-700">{studentNotes.length}</p>
            <p className="text-[10px] text-purple-600">Notes suivi</p>
          </CardContent>
        </Card>
        <Card className="bg-rose-50 border-rose-200">
          <CardContent className="p-3 text-center">
            <CalendarClock className="w-5 h-5 mx-auto mb-1 text-rose-600" />
            <p className="text-xl font-bold text-rose-700">{upcomingMeetings}</p>
            <p className="text-[10px] text-rose-600">Réunions</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-7 h-auto">
          <TabsTrigger value="liaison" className="flex flex-col sm:flex-row items-center gap-1 py-2 text-xs">
            <Mail className="w-4 h-4" />
            <span className="hidden sm:inline">Liaison</span>
          </TabsTrigger>
          <TabsTrigger value="homework" className="flex flex-col sm:flex-row items-center gap-1 py-2 text-xs">
            <BookOpen className="w-4 h-4" />
            <span className="hidden sm:inline">Devoirs</span>
          </TabsTrigger>
          <TabsTrigger value="life" className="flex flex-col sm:flex-row items-center gap-1 py-2 text-xs">
            <Camera className="w-4 h-4" />
            <span className="hidden sm:inline">Vie</span>
          </TabsTrigger>
          <TabsTrigger value="albums" className="flex flex-col sm:flex-row items-center gap-1 py-2 text-xs">
            <Images className="w-4 h-4" />
            <span className="hidden sm:inline">Albums</span>
          </TabsTrigger>
          <TabsTrigger value="tracking" className="flex flex-col sm:flex-row items-center gap-1 py-2 text-xs">
            <UserCheck className="w-4 h-4" />
            <span className="hidden sm:inline">Suivi</span>
          </TabsTrigger>
          <TabsTrigger value="meetings" className="flex flex-col sm:flex-row items-center gap-1 py-2 text-xs">
            <CalendarClock className="w-4 h-4" />
            <span className="hidden sm:inline">Réunions</span>
          </TabsTrigger>
          <TabsTrigger value="codes" className="flex flex-col sm:flex-row items-center gap-1 py-2 text-xs">
            <Key className="w-4 h-4" />
            <span className="hidden sm:inline">Codes</span>
          </TabsTrigger>
        </TabsList>

        {/* Carnet de liaison */}
        <TabsContent value="liaison" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowNewMessage(true)} data-testid="new-message-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau message
            </Button>
          </div>

          <ScrollArea className="h-[400px]">
            {messages.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Mail className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>Aucun message</p>
              </div>
            ) : (
              <div className="space-y-2">
                {messages.map((message) => (
                  <MessageCard
                    key={message.id}
                    message={message}
                    onPublish={() => handlePublishMessage(message.id)}
                    onDelete={() => handleDeleteMessage(message.id)}
                    onViewSignatures={() => handleViewSignatures(message)}
                  />
                ))}
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        {/* Cahier de texte */}
        <TabsContent value="homework" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowNewHomework(true)} data-testid="new-homework-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau devoir
            </Button>
          </div>

          <ScrollArea className="h-[400px]">
            {homework.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <BookOpen className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>Aucun devoir à venir</p>
              </div>
            ) : (
              <div className="space-y-2">
                {homework.map((hw) => (
                  <HomeworkCard
                    key={hw.id}
                    homework={hw}
                    onDelete={() => handleDeleteHomework(hw.id)}
                  />
                ))}
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        {/* Cahier de vie */}
        <TabsContent value="life" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowNewLifeEntry(true)} data-testid="new-life-entry-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau moment
            </Button>
          </div>

          <ScrollArea className="h-[400px]">
            {lifeEntries.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Camera className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>Aucun moment de classe</p>
              </div>
            ) : (
              <div className="space-y-2">
                {lifeEntries.map((entry) => (
                  <LifeEntryCard
                    key={entry.id}
                    entry={entry}
                    onPublish={async () => {
                      try {
                        await publishLifeEntry(entry.id);
                        toast.success("Publié !");
                        refreshLifeEntries();
                      } catch (err) {
                        toast.error("Erreur");
                      }
                    }}
                    onDelete={() => handleDeleteLifeEntry(entry.id)}
                  />
                ))}
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        {/* Suivi élèves */}
        <TabsContent value="tracking" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowNewNote(true)} data-testid="new-note-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle note
            </Button>
          </div>

          <ScrollArea className="h-[400px]">
            {studentNotes.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <UserCheck className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>Aucune note de suivi</p>
              </div>
            ) : (
              <div className="space-y-2">
                {studentNotes.map((note) => (
                  <StudentNoteCard
                    key={note.id}
                    note={note}
                    onDelete={() => handleDeleteNote(note.id)}
                  />
                ))}
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        {/* Réunions parents */}
        <TabsContent value="meetings" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowNewMeeting(true)} data-testid="new-meeting-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle réunion
            </Button>
          </div>

          <ScrollArea className="h-[400px]">
            {meetings.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CalendarClock className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>Aucune réunion planifiée</p>
              </div>
            ) : (
              <div className="space-y-2">
                {meetings.map((meeting) => (
                  <MeetingCard
                    key={meeting.id}
                    meeting={meeting}
                    onStatusChange={async (status) => {
                      try {
                        await updateMeetingStatus(meeting.id, status);
                        toast.success("Statut mis à jour");
                        refreshMeetings();
                      } catch (err) {
                        toast.error("Erreur");
                      }
                    }}
                    onDelete={() => handleDeleteMeeting(meeting.id)}
                  />
                ))}
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        {/* Albums / Livre photo */}
        <TabsContent value="albums" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowNewAlbum(true)} data-testid="new-album-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvel album
            </Button>
          </div>

          <ScrollArea className="h-[400px]">
            {albums.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Images className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>Aucun album</p>
                <p className="text-xs mt-1">Créez des albums pour partager les moments de classe</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {albums.map((album) => (
                  <AlbumCard
                    key={album.id}
                    album={album}
                    onPublish={() => handlePublishAlbum(album.id)}
                    onDelete={() => handleDeleteAlbum(album.id)}
                    onView={() => {
                      setSelectedAlbum(album);
                      setShowAlbumDetail(true);
                    }}
                  />
                ))}
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        {/* Codes Famille */}
        <TabsContent value="codes" className="space-y-4">
          <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
            <h4 className="font-semibold text-indigo-800 mb-2 flex items-center gap-2">
              <Key className="w-4 h-4" />
              Codes d'accès famille
            </h4>
            <p className="text-sm text-indigo-600">
              Générez des codes uniques pour permettre aux parents d'accéder au cartable de leur enfant.
            </p>
          </div>

          {/* Generate new code */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Générer un nouveau code</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {students.map((student) => {
                  const hasCode = familyCodes.some(c => c.student_id === student.id && c.is_active);
                  return (
                    <Button
                      key={student.id}
                      variant={hasCode ? "secondary" : "outline"}
                      size="sm"
                      onClick={() => !hasCode && handleGenerateFamilyCode(student.id)}
                      disabled={hasCode}
                      className="text-xs"
                    >
                      {student.student_name}
                      {hasCode && <Check className="w-3 h-3 ml-1 text-green-500" />}
                    </Button>
                  );
                })}
              </div>
              {students.length === 0 && (
                <p className="text-sm text-slate-500">Ajoutez d'abord des élèves dans la gestion de classe</p>
              )}
            </CardContent>
          </Card>

          {/* Existing codes */}
          <ScrollArea className="h-[300px]">
            {familyCodes.filter(c => c.is_active).length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Key className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>Aucun code actif</p>
              </div>
            ) : (
              <div className="space-y-2">
                {familyCodes.filter(c => c.is_active).map((code) => (
                  <FamilyCodeCard
                    key={code.id}
                    code={code}
                    onCopy={() => handleCopyCode(code.code)}
                    onRegenerate={() => handleRegenerateFamilyCode(code.id)}
                    onRevoke={() => handleRevokeFamilyCode(code.id)}
                  />
                ))}
              </div>
            )}
          </ScrollArea>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <NewMessageDialog
        open={showNewMessage}
        onOpenChange={setShowNewMessage}
        students={students}
        onSave={refreshMessages}
      />

      <NewHomeworkDialog
        open={showNewHomework}
        onOpenChange={setShowNewHomework}
        students={students}
        teacherCycles={teacherCycles}
        onSave={refreshHomework}
      />

      <NewLifeEntryDialog
        open={showNewLifeEntry}
        onOpenChange={setShowNewLifeEntry}
        students={students}
        onSave={refreshLifeEntries}
      />

      <NewStudentNoteDialog
        open={showNewNote}
        onOpenChange={setShowNewNote}
        students={students}
        onSave={refreshNotes}
      />

      <NewMeetingDialog
        open={showNewMeeting}
        onOpenChange={setShowNewMeeting}
        students={students}
        onSave={refreshMeetings}
      />

      <NewAlbumDialog
        open={showNewAlbum}
        onOpenChange={setShowNewAlbum}
        onSave={refreshAlbums}
      />

      <AlbumDetailDialog
        open={showAlbumDetail}
        onOpenChange={setShowAlbumDetail}
        album={selectedAlbum}
        onRefresh={async () => {
          await refreshAlbums();
          // Refresh selected album
          if (selectedAlbum) {
            try {
              const res = await getAlbum(selectedAlbum.id);
              setSelectedAlbum(res.data);
            } catch (err) {
              console.error("Error refreshing album:", err);
            }
          }
        }}
      />

      {/* Signatures Dialog */}
      <ResponsiveModal
        open={showSignatures}
        onOpenChange={setShowSignatures}
        title="Signatures"
      >
        <div className="py-4">
          {signatures.length === 0 ? (
            <p className="text-center text-slate-500">Aucune signature requise</p>
          ) : (
            <div className="space-y-2">
              {signatures.map((sig) => (
                <div key={sig.student_id} className="flex items-center justify-between p-2 rounded bg-slate-50">
                  <span className="font-medium">{sig.student_name}</span>
                  {sig.signed ? (
                    <Badge className="bg-green-100 text-green-700">
                      <Check className="w-3 h-3 mr-1" />
                      Signé
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-slate-500">
                      En attente
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </ResponsiveModal>
    </div>
  );
}

// ============================================================
// Card Components
// ============================================================

function MessageCard({ message, onPublish, onDelete, onViewSignatures }) {
  const priorityInfo = PRIORITY_OPTIONS.find(p => p.value === message.priority) || PRIORITY_OPTIONS[0];
  const isDraft = message.status === "draft";

  return (
    <Card className={isDraft ? "border-dashed border-slate-300" : ""}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h4 className="font-medium text-slate-800 truncate">{message.title}</h4>
              <Badge className={priorityInfo.color}>{priorityInfo.label}</Badge>
              {isDraft && <Badge variant="outline">Brouillon</Badge>}
            </div>
            <p className="text-sm text-slate-500 line-clamp-2">{message.content}</p>
            <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {format(parseISO(message.created_at), "d MMM yyyy", { locale: fr })}
              </span>
              {message.requires_signature && (
                <span className="flex items-center gap-1 text-amber-600">
                  <FileSignature className="w-3 h-3" />
                  Signature requise
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            {isDraft ? (
              <Button size="sm" onClick={onPublish}>
                <Send className="w-3 h-3 mr-1" />
                Publier
              </Button>
            ) : message.requires_signature ? (
              <Button size="sm" variant="outline" onClick={onViewSignatures}>
                <Eye className="w-3 h-3 mr-1" />
                Signatures
              </Button>
            ) : null}
            <Button size="sm" variant="ghost" className="text-red-500" onClick={onDelete}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function HomeworkCard({ homework, onDelete }) {
  const isOverdue = new Date(homework.due_date) < new Date();

  return (
    <Card className={isOverdue ? "border-red-300 bg-red-50/30" : ""}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h4 className="font-medium text-slate-800">{homework.title}</h4>
              {homework.subject_label && (
                <Badge variant="outline">{homework.subject_label}</Badge>
              )}
              {homework.is_optional && (
                <Badge variant="secondary">Facultatif</Badge>
              )}
            </div>
            {homework.description && (
              <p className="text-sm text-slate-500 line-clamp-2">{homework.description}</p>
            )}
            <div className="flex items-center gap-3 mt-2 text-xs">
              <span className={`flex items-center gap-1 ${isOverdue ? "text-red-600" : "text-slate-500"}`}>
                <Calendar className="w-3 h-3" />
                Pour le {format(parseISO(homework.due_date), "d MMMM", { locale: fr })}
              </span>
              {homework.estimated_duration && (
                <span className="flex items-center gap-1 text-slate-400">
                  <Clock className="w-3 h-3" />
                  ~{homework.estimated_duration} min
                </span>
              )}
            </div>
          </div>
          <Button size="sm" variant="ghost" className="text-red-500" onClick={onDelete}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function LifeEntryCard({ entry, onPublish, onDelete }) {
  const typeInfo = LIFE_ENTRY_TYPES.find(t => t.value === entry.entry_type) || LIFE_ENTRY_TYPES[0];

  return (
    <Card className={!entry.is_published ? "border-dashed border-amber-300" : ""}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-lg">{typeInfo.emoji}</span>
              <h4 className="font-medium text-slate-800">{entry.title}</h4>
              <Badge variant="outline">{typeInfo.label}</Badge>
              {!entry.is_published && <Badge className="bg-amber-100 text-amber-700">Brouillon</Badge>}
            </div>
            <p className="text-sm text-slate-500 line-clamp-2">{entry.content}</p>
            <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {format(parseISO(entry.date), "d MMMM yyyy", { locale: fr })}
              </span>
              {entry.photos?.length > 0 && (
                <span className="flex items-center gap-1">
                  <ImageIcon className="w-3 h-3" />
                  {entry.photos.length} photo(s)
                </span>
              )}
              {entry.likes?.length > 0 && (
                <span className="flex items-center gap-1 text-rose-500">
                  <Heart className="w-3 h-3 fill-current" />
                  {entry.likes.length}
                </span>
              )}
              {entry.comments?.length > 0 && (
                <span className="flex items-center gap-1">
                  <MessageCircle className="w-3 h-3" />
                  {entry.comments.length}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            {!entry.is_published && (
              <Button size="sm" onClick={onPublish}>
                <Send className="w-3 h-3 mr-1" />
                Publier
              </Button>
            )}
            <Button size="sm" variant="ghost" className="text-red-500" onClick={onDelete}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StudentNoteCard({ note, onDelete }) {
  const typeInfo = NOTE_TYPES.find(t => t.value === note.note_type) || NOTE_TYPES[0];

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h4 className="font-medium text-slate-800">{note.title}</h4>
              <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
              {note.is_private && (
                <Badge variant="outline" className="text-slate-500">Privé</Badge>
              )}
            </div>
            <p className="text-sm text-indigo-600 mb-1">{note.student_name}</p>
            <p className="text-sm text-slate-500 line-clamp-2">{note.content}</p>
            <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
              <Calendar className="w-3 h-3" />
              {format(parseISO(note.date), "d MMMM yyyy", { locale: fr })}
            </div>
          </div>
          <Button size="sm" variant="ghost" className="text-red-500" onClick={onDelete}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function MeetingCard({ meeting, onStatusChange, onDelete }) {
  const statusInfo = MEETING_STATUSES.find(s => s.value === meeting.status) || MEETING_STATUSES[0];

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h4 className="font-medium text-slate-800">{meeting.student_name}</h4>
              <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
              <Badge variant="outline" className="text-xs">
                {meeting.meeting_type === "collectif" ? "Collectif" : "Individuel"}
              </Badge>
            </div>
            <div className="flex items-center gap-3 text-sm text-slate-600">
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {format(parseISO(meeting.date), "EEEE d MMMM", { locale: fr })}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {meeting.time} ({meeting.duration_minutes} min)
              </span>
            </div>
            {meeting.location && (
              <p className="text-xs text-slate-400 mt-1">Lieu : {meeting.location}</p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            {meeting.status === "planifie" && (
              <Button size="sm" variant="outline" onClick={() => onStatusChange("confirme")}>
                <Check className="w-3 h-3 mr-1" />
                Confirmer
              </Button>
            )}
            {meeting.status === "confirme" && (
              <Button size="sm" variant="outline" onClick={() => onStatusChange("termine")}>
                <Check className="w-3 h-3 mr-1" />
                Terminer
              </Button>
            )}
            <Button size="sm" variant="ghost" className="text-red-500" onClick={onDelete}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AlbumCard({ album, onPublish, onDelete, onView }) {
  const photoCount = album.photo_count || album.photos?.length || 0;
  
  return (
    <Card 
      className={`cursor-pointer hover:shadow-md transition-shadow ${!album.is_published ? "border-dashed border-amber-300" : ""}`}
      onClick={onView}
    >
      <div className="aspect-square bg-gradient-to-br from-indigo-100 to-purple-100 rounded-t-lg flex items-center justify-center relative overflow-hidden">
        {album.cover_photo_id ? (
          <img 
            src={getFileUrl(album.cover_photo_id)} 
            alt={album.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <Images className="w-12 h-12 text-indigo-300" />
        )}
        {!album.is_published && (
          <Badge className="absolute top-2 right-2 bg-amber-500 text-white text-[10px]">
            Brouillon
          </Badge>
        )}
      </div>
      <CardContent className="p-3">
        <h4 className="font-medium text-sm text-slate-800 truncate">{album.title}</h4>
        <p className="text-xs text-slate-400 mt-1">{photoCount} photo(s)</p>
        <div className="flex items-center gap-1 mt-2" onClick={(e) => e.stopPropagation()}>
          {!album.is_published && (
            <Button size="sm" variant="ghost" className="h-7 px-2 text-green-600" onClick={onPublish}>
              <Send className="w-3 h-3" />
            </Button>
          )}
          <Button size="sm" variant="ghost" className="h-7 px-2 text-red-500" onClick={onDelete}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function FamilyCodeCard({ code, onCopy, onRegenerate, onRevoke }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="font-medium text-slate-800">{code.student_name}</p>
            <div className="flex items-center gap-2 mt-1">
              <code className="text-lg font-mono font-bold text-indigo-600 bg-indigo-50 px-2 py-1 rounded">
                {code.code}
              </code>
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={onCopy}>
                <Copy className="w-3 h-3" />
              </Button>
            </div>
            <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
              <span>Créé le {format(parseISO(code.created_at), "d MMM yyyy", { locale: fr })}</span>
              {code.use_count > 0 && (
                <span>Utilisé {code.use_count}x</span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onRegenerate}>
              <RefreshCw className="w-3 h-3 mr-1" />
              Régénérer
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" onClick={onRevoke}>
              <Trash2 className="w-3 h-3 mr-1" />
              Révoquer
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================
// Dialog Components
// ============================================================

function NewMessageDialog({ open, onOpenChange, students, onSave }) {
  const [form, setForm] = useState({
    title: "",
    content: "",
    priority: "normal",
    requires_signature: false,
    target_student_ids: [],
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async (publish = false) => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Titre et contenu obligatoires");
      return;
    }

    setSaving(true);
    try {
      const res = await createLiaisonMessage(form);
      if (publish && res.data?.id) {
        await publishLiaisonMessage(res.data.id);
      }
      toast.success(publish ? "Message publié" : "Brouillon enregistré");
      setForm({ title: "", content: "", priority: "normal", requires_signature: false, target_student_ids: [] });
      onOpenChange(false);
      onSave();
    } catch (err) {
      console.error("Erreur création message:", err);
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title="Nouveau message"
      footer={
        <div className="flex gap-2 w-full">
          <Button variant="outline" onClick={() => handleSave(false)} disabled={saving} className="flex-1">
            Brouillon
          </Button>
          <Button onClick={() => handleSave(true)} disabled={saving} className="flex-1">
            <Send className="w-4 h-4 mr-2" />
            Publier
          </Button>
        </div>
      }
    >
      <div className="space-y-4 py-4">
        <div>
          <label className="text-sm font-medium">Titre *</label>
          <Input
            value={form.title}
            onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
            placeholder="Objet du message..."
            className="mt-1"
          />
        </div>
        <div>
          <label className="text-sm font-medium">Contenu *</label>
          <Textarea
            value={form.content}
            onChange={(e) => setForm(prev => ({ ...prev, content: e.target.value }))}
            placeholder="Rédigez votre message..."
            className="mt-1"
            rows={4}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Priorité</label>
            <Select
              value={form.priority}
              onValueChange={(v) => setForm(prev => ({ ...prev, priority: v }))}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 mt-6">
            <Checkbox
              id="requires_signature"
              checked={form.requires_signature}
              onCheckedChange={(c) => setForm(prev => ({ ...prev, requires_signature: c }))}
            />
            <label htmlFor="requires_signature" className="text-sm">
              Signature requise
            </label>
          </div>
        </div>
      </div>
    </ResponsiveModal>
  );
}

function NewHomeworkDialog({ open, onOpenChange, students, teacherCycles, onSave }) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    subject: "",
    subject_label: "",
    due_date: "",
    estimated_duration: null,
    is_optional: false,
    student_ids: [],
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.title.trim() || !form.due_date) {
      toast.error("Titre et date obligatoires");
      return;
    }

    setSaving(true);
    try {
      await createHomework({
        ...form,
        estimated_duration: form.estimated_duration ? parseInt(form.estimated_duration) : null,
      });
      toast.success("Devoir créé");
      setForm({ title: "", description: "", subject: "", subject_label: "", due_date: "", estimated_duration: null, is_optional: false, student_ids: [] });
      onOpenChange(false);
      onSave();
    } catch (err) {
      console.error("Erreur création devoir:", err);
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title="Nouveau devoir"
      footer={
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            Créer
          </Button>
        </div>
      }
    >
      <div className="space-y-4 py-4">
        <div>
          <label className="text-sm font-medium">Titre *</label>
          <Input
            value={form.title}
            onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
            placeholder="Ex: Apprendre la leçon..."
            className="mt-1"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Matière</label>
            <DynamicSubjectSelector
              value={form.subject}
              onChange={(id, subject) => setForm(prev => ({
                ...prev,
                subject: id,
                subject_label: subject?.label || ""
              }))}
              cycles={teacherCycles}
              className="w-full mt-1"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Pour le *</label>
            <Input
              type="date"
              value={form.due_date}
              onChange={(e) => setForm(prev => ({ ...prev, due_date: e.target.value }))}
              className="mt-1"
            />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium">Description</label>
          <Textarea
            value={form.description}
            onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Consignes, pages à lire..."
            className="mt-1"
            rows={3}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Durée estimée (min)</label>
            <Input
              type="number"
              value={form.estimated_duration || ""}
              onChange={(e) => setForm(prev => ({ ...prev, estimated_duration: e.target.value }))}
              placeholder="15"
              className="mt-1"
            />
          </div>
          <div className="flex items-center gap-2 mt-6">
            <Checkbox
              id="is_optional"
              checked={form.is_optional}
              onCheckedChange={(c) => setForm(prev => ({ ...prev, is_optional: c }))}
            />
            <label htmlFor="is_optional" className="text-sm">
              Devoir facultatif
            </label>
          </div>
        </div>
      </div>
    </ResponsiveModal>
  );
}

function NewLifeEntryDialog({ open, onOpenChange, students, onSave }) {
  const [form, setForm] = useState({
    title: "",
    content: "",
    entry_type: "moment",
    date: new Date().toISOString().split("T")[0],
    photos: [],
    attachments: [],
    is_published: false,
    student_ids: [],
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (e, type) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    try {
      for (const file of files) {
        const res = await uploadFile(file, "life-entries");
        const fileData = {
          id: res.data.id,
          url: res.data.url,
          name: res.data.filename,
        };
        
        if (type === "photo") {
          setForm(prev => ({ ...prev, photos: [...prev.photos, fileData] }));
        } else {
          setForm(prev => ({ ...prev, attachments: [...prev.attachments, fileData] }));
        }
      }
      toast.success("Fichier(s) ajouté(s)");
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (type, id) => {
    if (type === "photo") {
      setForm(prev => ({ ...prev, photos: prev.photos.filter(p => p.id !== id) }));
    } else {
      setForm(prev => ({ ...prev, attachments: prev.attachments.filter(a => a.id !== id) }));
    }
  };

  const handleSave = async (publish = false) => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Titre et contenu obligatoires");
      return;
    }

    setSaving(true);
    try {
      const res = await createLifeEntry({ ...form, is_published: publish });
      toast.success(publish ? "Moment publié" : "Brouillon enregistré");
      setForm({
        title: "",
        content: "",
        entry_type: "moment",
        date: new Date().toISOString().split("T")[0],
        photos: [],
        attachments: [],
        is_published: false,
        student_ids: [],
      });
      onOpenChange(false);
      onSave();
    } catch (err) {
      console.error("Erreur création entrée:", err);
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title="Nouveau moment de classe"
      footer={
        <div className="flex gap-2 w-full">
          <Button variant="outline" onClick={() => handleSave(false)} disabled={saving} className="flex-1">
            Brouillon
          </Button>
          <Button onClick={() => handleSave(true)} disabled={saving} className="flex-1">
            <Send className="w-4 h-4 mr-2" />
            Publier
          </Button>
        </div>
      }
    >
      <div className="space-y-4 py-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Type</label>
            <Select
              value={form.entry_type}
              onValueChange={(v) => setForm(prev => ({ ...prev, entry_type: v }))}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LIFE_ENTRY_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.emoji} {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium">Date</label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))}
              className="mt-1"
            />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium">Titre *</label>
          <Input
            value={form.title}
            onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
            placeholder="Un beau moment..."
            className="mt-1"
          />
        </div>
        <div>
          <label className="text-sm font-medium">Description *</label>
          <Textarea
            value={form.content}
            onChange={(e) => setForm(prev => ({ ...prev, content: e.target.value }))}
            placeholder="Racontez ce moment..."
            className="mt-1"
            rows={4}
          />
        </div>
        
        {/* Photos */}
        <div>
          <label className="text-sm font-medium flex items-center gap-2">
            <ImageIcon className="w-4 h-4" />
            Photos
          </label>
          <div className="flex flex-wrap gap-2 mt-2">
            {form.photos.map((photo) => (
              <div key={photo.id} className="relative group">
                <div className="w-16 h-16 bg-slate-100 rounded flex items-center justify-center">
                  <ImageIcon className="w-6 h-6 text-slate-400" />
                </div>
                <button
                  onClick={() => removeFile("photo", photo.id)}
                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            <label className="w-16 h-16 border-2 border-dashed border-slate-300 rounded flex items-center justify-center cursor-pointer hover:border-indigo-400 transition-colors">
              {uploading ? (
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              ) : (
                <Plus className="w-5 h-5 text-slate-400" />
              )}
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => handleFileUpload(e, "photo")}
                disabled={uploading}
              />
            </label>
          </div>
        </div>

        {/* Attachments */}
        <div>
          <label className="text-sm font-medium flex items-center gap-2">
            <Paperclip className="w-4 h-4" />
            Pièces jointes
          </label>
          <div className="space-y-1 mt-2">
            {form.attachments.map((att) => (
              <div key={att.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                <FileText className="w-4 h-4 text-slate-400" />
                <span className="text-sm flex-1 truncate">{att.name}</span>
                <button
                  onClick={() => removeFile("attachment", att.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            <label className="flex items-center gap-2 p-2 border-2 border-dashed border-slate-300 rounded cursor-pointer hover:border-indigo-400 transition-colors">
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Upload className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-500">Ajouter un fichier</span>
                </>
              )}
              <input
                type="file"
                className="hidden"
                onChange={(e) => handleFileUpload(e, "attachment")}
                disabled={uploading}
              />
            </label>
          </div>
        </div>
      </div>
    </ResponsiveModal>
  );
}

function NewStudentNoteDialog({ open, onOpenChange, students, onSave }) {
  const [form, setForm] = useState({
    student_id: "",
    note_type: "observation",
    title: "",
    content: "",
    date: new Date().toISOString().split("T")[0],
    is_private: true,
    attachments: [],
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.student_id || !form.title.trim() || !form.content.trim()) {
      toast.error("Élève, titre et contenu obligatoires");
      return;
    }

    setSaving(true);
    try {
      await createStudentNote(form);
      toast.success("Note créée");
      setForm({
        student_id: "",
        note_type: "observation",
        title: "",
        content: "",
        date: new Date().toISOString().split("T")[0],
        is_private: true,
        attachments: [],
      });
      onOpenChange(false);
      onSave();
    } catch (err) {
      console.error("Erreur création note:", err);
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title="Nouvelle note de suivi"
      footer={
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            Créer
          </Button>
        </div>
      }
    >
      <div className="space-y-4 py-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Élève *</label>
            <Select
              value={form.student_id}
              onValueChange={(v) => setForm(prev => ({ ...prev, student_id: v }))}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Choisir..." />
              </SelectTrigger>
              <SelectContent>
                {students.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.student_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium">Type</label>
            <Select
              value={form.note_type}
              onValueChange={(v) => setForm(prev => ({ ...prev, note_type: v }))}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {NOTE_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Date</label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))}
              className="mt-1"
            />
          </div>
          <div className="flex items-center gap-2 mt-6">
            <Checkbox
              id="is_private"
              checked={form.is_private}
              onCheckedChange={(c) => setForm(prev => ({ ...prev, is_private: c }))}
            />
            <label htmlFor="is_private" className="text-sm">
              Note privée
            </label>
          </div>
        </div>
        <div>
          <label className="text-sm font-medium">Titre *</label>
          <Input
            value={form.title}
            onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
            placeholder="Titre de la note..."
            className="mt-1"
          />
        </div>
        <div>
          <label className="text-sm font-medium">Contenu *</label>
          <Textarea
            value={form.content}
            onChange={(e) => setForm(prev => ({ ...prev, content: e.target.value }))}
            placeholder="Observations, remarques..."
            className="mt-1"
            rows={4}
          />
        </div>
      </div>
    </ResponsiveModal>
  );
}

function NewMeetingDialog({ open, onOpenChange, students, onSave }) {
  const [form, setForm] = useState({
    student_id: "",
    meeting_type: "individuel",
    date: "",
    time: "17:00",
    duration_minutes: 15,
    location: "Classe",
    notes: "",
    status: "planifie",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.student_id || !form.date || !form.time) {
      toast.error("Élève, date et heure obligatoires");
      return;
    }

    setSaving(true);
    try {
      await createMeeting(form);
      toast.success("Réunion planifiée");
      setForm({
        student_id: "",
        meeting_type: "individuel",
        date: "",
        time: "17:00",
        duration_minutes: 15,
        location: "Classe",
        notes: "",
        status: "planifie",
      });
      onOpenChange(false);
      onSave();
    } catch (err) {
      console.error("Erreur création réunion:", err);
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title="Nouvelle réunion parents"
      footer={
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            Planifier
          </Button>
        </div>
      }
    >
      <div className="space-y-4 py-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Élève *</label>
            <Select
              value={form.student_id}
              onValueChange={(v) => setForm(prev => ({ ...prev, student_id: v }))}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Choisir..." />
              </SelectTrigger>
              <SelectContent>
                {students.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.student_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium">Type</label>
            <Select
              value={form.meeting_type}
              onValueChange={(v) => setForm(prev => ({ ...prev, meeting_type: v }))}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="individuel">Individuel</SelectItem>
                <SelectItem value="collectif">Collectif</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-medium">Date *</label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))}
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Heure *</label>
            <Input
              type="time"
              value={form.time}
              onChange={(e) => setForm(prev => ({ ...prev, time: e.target.value }))}
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Durée (min)</label>
            <Input
              type="number"
              value={form.duration_minutes}
              onChange={(e) => setForm(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 15 }))}
              className="mt-1"
            />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium">Lieu</label>
          <Input
            value={form.location}
            onChange={(e) => setForm(prev => ({ ...prev, location: e.target.value }))}
            placeholder="Classe, salle des maîtres..."
            className="mt-1"
          />
        </div>
        <div>
          <label className="text-sm font-medium">Notes</label>
          <Textarea
            value={form.notes}
            onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Points à aborder..."
            className="mt-1"
            rows={3}
          />
        </div>
      </div>
    </ResponsiveModal>
  );
}

function NewAlbumDialog({ open, onOpenChange, onSave }) {
  const [form, setForm] = useState({
    title: "",
    description: "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async (publish = false) => {
    if (!form.title.trim()) {
      toast.error("Titre obligatoire");
      return;
    }

    setSaving(true);
    try {
      const res = await createAlbum({ ...form, is_published: publish });
      toast.success(publish ? "Album créé et publié" : "Album créé");
      setForm({ title: "", description: "" });
      onOpenChange(false);
      onSave();
    } catch (err) {
      console.error("Erreur création album:", err);
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title="Nouvel album"
      footer={
        <div className="flex gap-2 w-full">
          <Button variant="outline" onClick={() => handleSave(false)} disabled={saving} className="flex-1">
            Créer brouillon
          </Button>
          <Button onClick={() => handleSave(true)} disabled={saving} className="flex-1">
            <Send className="w-4 h-4 mr-2" />
            Créer et publier
          </Button>
        </div>
      }
    >
      <div className="space-y-4 py-4">
        <div>
          <label className="text-sm font-medium">Titre de l'album *</label>
          <Input
            value={form.title}
            onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
            placeholder="Sortie au musée, Projet jardinage..."
            className="mt-1"
          />
        </div>
        <div>
          <label className="text-sm font-medium">Description</label>
          <Textarea
            value={form.description}
            onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Décrivez cet album..."
            className="mt-1"
            rows={3}
          />
        </div>
      </div>
    </ResponsiveModal>
  );
}

function AlbumDetailDialog({ open, onOpenChange, album, onRefresh }) {
  const [photos, setPhotos] = useState(album?.photos || []);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (album) {
      setPhotos(album.photos || []);
    }
  }, [album]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      // Upload file first
      const uploadRes = await uploadFile(file, "albums");
      
      // Add to album
      await addPhotoToAlbum(album.id, {
        file_id: uploadRes.data.id,
        caption: "",
      });
      
      toast.success("Photo ajoutée");
      onRefresh();
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleRemovePhoto = async (photoId) => {
    try {
      await removePhotoFromAlbum(album.id, photoId);
      toast.success("Photo supprimée");
      onRefresh();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  if (!album) return null;

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title={album.title}
    >
      <div className="space-y-4 py-4">
        {album.description && (
          <p className="text-sm text-slate-500">{album.description}</p>
        )}
        
        {/* Photos Grid */}
        <div className="grid grid-cols-3 gap-2">
          {photos.map((photo) => (
            <div key={photo.id} className="relative aspect-square group">
              <img
                src={getFileUrl(photo.file_id)}
                alt={photo.caption || "Photo"}
                className="w-full h-full object-cover rounded-lg"
              />
              <button
                onClick={() => handleRemovePhoto(photo.id)}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          
          {/* Upload button */}
          <label className="aspect-square border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-indigo-400 transition-colors">
            {uploading ? (
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            ) : (
              <>
                <Plus className="w-6 h-6 text-slate-400" />
                <span className="text-xs text-slate-400 mt-1">Ajouter</span>
              </>
            )}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileUpload}
              disabled={uploading}
            />
          </label>
        </div>

        <p className="text-xs text-slate-400 text-center">
          {photos.length} photo(s) dans cet album
        </p>
      </div>
    </ResponsiveModal>
  );
}

export default CartableModule;
